import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Heart, Building2, Calendar, Users, UserCheck, Search, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';

// Firebase & Auth imports
import { collection, query, where, getDocs, doc, getDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface FavoriteWithDetails {
  id: string;
  favorite_type: 'deal' | 'partner' | 'consultant';
  created_at: string;
  deal?: {
    id: string;
    business_name: string;
    business_description: string | null;
    asking_price: number | null;
    currency: string | null;
    current_stage: string;
  };
  partner?: {
    id: string;
    name: string;
    type: string;
    specialty: string | null;
    location: string | null;
    rating: number;
  };
  consultant?: {
    id: string;
    name: string;
    specialty: string;
    location: string | null;
    rating: number;
    availability: string;
  };
}

const Favorites = () => {
  const { user } = useAuth(); // Récupération de l'utilisateur via le context
  const [favorites, setFavorites] = useState<FavoriteWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchFavorites = async () => {
    // Intégration de la consigne pour fetchFavorites
    if (!user?.id) { 
      setLoading(false); 
      return; 
    }

    try {
      setLoading(true);
      const snap = await getDocs(
        query(collection(db, 'favorites'), where('userId', '==', user.id), orderBy('createdAt', 'desc'))
      );

      const results: FavoriteWithDetails[] = [];
      
      for (const d of snap.docs) {
        const data = d.data();
        const fav: FavoriteWithDetails = {
          id: d.id,
          favorite_type: data.favoriteType || data.favorite_type,
          created_at: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        };

        if (fav.favorite_type === 'deal' && data.targetId) {
          const dealDoc = await getDoc(doc(db, 'deals', data.targetId));
          if (dealDoc.exists()) fav.deal = { id: dealDoc.id, ...dealDoc.data() } as any;
        } else if (fav.favorite_type === 'partner' && data.targetId) {
          const pDoc = await getDoc(doc(db, 'partners', data.targetId));
          if (pDoc.exists()) fav.partner = { id: pDoc.id, ...pDoc.data() } as any;
        } else if (fav.favorite_type === 'consultant' && data.targetId) {
          const cDoc = await getDoc(doc(db, 'consultants', data.targetId));
          if (cDoc.exists()) fav.consultant = { id: cDoc.id, ...cDoc.data() } as any;
        }
        results.push(fav);
      }
      setFavorites(results);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger vos favoris',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [user?.id]); // On recharge si l'ID utilisateur change

  const removeFavorite = async (favoriteId: string) => {
    try {
      // Intégration de la consigne pour removeFavorite
      await deleteDoc(doc(db, 'favorites', favoriteId));

      setFavorites(favorites.filter(f => f.id !== favoriteId));
      toast({
        title: 'Succès',
        description: 'Favori supprimé',
      });
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le favori',
        variant: 'destructive',
      });
    }
  };

  const filteredFavorites = favorites.filter((fav) => {
    const matchesTab = activeTab === 'all' || fav.favorite_type === activeTab;
    
    let matchesSearch = false;
    if (fav.favorite_type === 'deal' && fav.deal) {
      matchesSearch = fav.deal.business_name.toLowerCase().includes(searchQuery.toLowerCase());
    } else if (fav.favorite_type === 'partner' && fav.partner) {
      matchesSearch = fav.partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        fav.partner.specialty?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
    } else if (fav.favorite_type === 'consultant' && fav.consultant) {
      matchesSearch = fav.consultant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        fav.consultant.specialty.toLowerCase().includes(searchQuery.toLowerCase());
    }
    
    if (!searchQuery) matchesSearch = true;
    
    return matchesTab && matchesSearch;
  });

  const counts = {
    all: favorites.length,
    deal: favorites.filter(f => f.favorite_type === 'deal').length,
    partner: favorites.filter(f => f.favorite_type === 'partner').length,
    consultant: favorites.filter(f => f.favorite_type === 'consultant').length,
  };

  const formatPrice = (price: number | null, currency: string | null) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: currency || 'CAD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(price);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <Skeleton className="h-12 w-64" />
            <Skeleton className="h-10 w-32" />
          </div>
          <Skeleton className="h-12 w-full" />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}><CardContent className="p-6"><Skeleton className="h-40 w-full" /></CardContent></Card>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Favoris</h1>
            <p className="text-muted-foreground">Vos éléments sauvegardés</p>
          </div>
          <Badge variant="secondary" className="text-lg px-4 py-2">
            <Heart className="h-4 w-4 mr-2 fill-current" />
            {favorites.length} sauvegardé{favorites.length > 1 ? 's' : ''}
          </Badge>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher dans vos favoris..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all">Tous ({counts.all})</TabsTrigger>
            <TabsTrigger value="deal" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Deals ({counts.deal})
            </TabsTrigger>
            <TabsTrigger value="partner" className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Partenaires ({counts.partner})
            </TabsTrigger>
            <TabsTrigger value="consultant" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" /> Consultants ({counts.consultant})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredFavorites.map((favorite) => {
                if (favorite.favorite_type === 'deal' && favorite.deal) {
                  return (
                    <Card key={favorite.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <Badge variant="outline" className="mb-2">Deal</Badge>
                            <CardTitle className="text-lg">{favorite.deal.business_name}</CardTitle>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500"
                            onClick={() => removeFavorite(favorite.id)}
                          >
                            <Heart className="h-5 w-5 fill-current" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-2 font-bold">
                            {formatPrice(favorite.deal.asking_price, favorite.deal.currency)}
                          </div>
                          <Badge variant="secondary">{favorite.deal.current_stage}</Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button className="flex-1" size="sm" onClick={() => navigate(`/deals/${favorite.deal?.id}`)}>Détails</Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }

                if (favorite.favorite_type === 'partner' && favorite.partner) {
                  return (
                    <Card key={favorite.id}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback>{favorite.partner.name[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <Badge variant="outline">Partenaire</Badge>
                            <CardTitle className="text-lg">{favorite.partner.name}</CardTitle>
                          </div>
                          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeFavorite(favorite.id)}>
                            <Heart className="h-5 w-5 fill-current" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Button className="w-full" size="sm" onClick={() => navigate('/partners')}>Voir profil</Button>
                      </CardContent>
                    </Card>
                  );
                }

                if (favorite.favorite_type === 'consultant' && favorite.consultant) {
                  return (
                    <Card key={favorite.id}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback>{favorite.consultant.name[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <Badge variant="outline">Consultant</Badge>
                            <CardTitle className="text-lg">{favorite.consultant.name}</CardTitle>
                          </div>
                          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeFavorite(favorite.id)}>
                            <Heart className="h-5 w-5 fill-current" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                         <Button className="w-full" size="sm" onClick={() => navigate('/consultants')}>Voir profil</Button>
                      </CardContent>
                    </Card>
                  );
                }
                return null;
              })}
            </div>

            {filteredFavorites.length === 0 && (
              <Card className="p-12 text-center">
                <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Aucun favori trouvé</h3>
                <Button onClick={() => navigate('/listings')}>Parcourir les listings</Button>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Favorites;